<!--
  An urls item in the actions list
-->

<template>
  <orderable-box
    v-bind="$attrs"
    v-on="$listeners"
    :icon="
      value.type === 'audio' ? 'el-icon-microphone' :
      value.type === 'image' ? 'el-icon-picture-outline' :
      'el-icon-link'">
    <template slot="content" v-if="value.type === 'audio'">
      Audio response
      <tooltip-icon placement="top">
        You can give several <em>audio files</em>, one will randomly be chosen.
      </tooltip-icon>
    </template>
    <template slot="content" v-else-if="value.type === 'image'">
      Image response
      <tooltip-icon placement="top">
        You can give several <em>image files</em>, one will randomly be chosen.
      </tooltip-icon>
    </template>
    <template slot="content" v-else>
      Response
      <tooltip-icon placement="top">
        You can give several <em>urls</em>, one will randomly be chosen.
      </tooltip-icon>
    </template>
    <el-form-item v-if="value.type === 'audio'"
      label="Language"
      :required="required && text"
      :prop="prop && prop+'.language'">
        <el-filterable-select
          v-model="language"
          :options="languageOptions"
          :disabled="disabled"
          :clearable="!text">
        </el-filterable-select>
        <el-checkbox style="margin-left: 20px"
          v-model="text">
          Text descriptions
        </el-checkbox>
    </el-form-item>
    <div v-for="(item, index) in items" :key="item.key">
      <el-form-small-item
        :prop="prop && prop+'.urls.'+index"
        :required="!item.temporary || required && items.length <= 1"
        :rules="URLValidator"
        field="file url">
        <el-button-group>
          <el-string-autocomplete
            v-model="item.value.url"
            :disabled="disabled"
            :placeholder="placeholder"
            :local-variables="localVariables"
            :context-variables="contextVariables"></el-string-autocomplete>
          <el-button
            :icon="item.temporary ? 'el-icon-circle-plus-outline' : 'el-icon-delete'"
            :disabled="disabled || item.temporary"
            @click="deleteItem(item.key)"
            :tabindex="-1"></el-button>
        </el-button-group>
      </el-form-small-item>
      <el-form-small-item v-if="text"
        style="margin-bottom: 10px !important"
        :prop="prop && prop+'.texts.'+index"
        :required="!item.temporary || required && items.length <= 1"
        :rules="OutputValidator"
        field="text">
        <el-button-group>
          <el-button type="info" disabled>
            text
          </el-button>
          <el-response-autocomplete
            v-model="item.value.text"
            placeholder="text"
            :local-variables="localVariables"
            :context-variables="contextVariables"
            :disabled="disabled"></el-response-autocomplete>
        </el-button-group>
      </el-form-small-item>
    </div>
  </orderable-box>
</template>

<script>
import { URLValidator, OutputValidator } from '../../utils/validators'

import OrderableBox from '../orderable/OrderableBox.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'
import ElResponseAutocomplete from '../element/ElResponseAutocomplete.vue'
import ElStringAutocomplete from '../element/ElStringAutocomplete.vue'

export default {
  components: {
    OrderableBox,
    ElFilterableSelect,
    ElResponseAutocomplete,
    ElStringAutocomplete,
  },
  name: 'UrlsActionEditor',
  props: {
    value: Object,
    placeholder: String,
    languageOptions: Array,
    localVariables: Array,
    contextVariables: Array,
    prop: String,
    required: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
  },
  data () {
    return {
      items: [],
      text: false,
      language: '',
      URLValidator,
      OutputValidator,
    }
  },
  watch: {
    value (value) { this.updateItems(value) },
    items: {
      handler: 'itemsUpdated',
      deep: true,
    },
    text: 'itemsUpdated',
    language: 'itemsUpdated',
  },
  created () {
    this.updateItems(this.value)
  },
  methods: {
    updateItems (value) {
      /* Called when the source value is updated
      Checks that this.items is similar to the value,
      and updates this.items if not */
      let items = this.items.filter(t => !t.temporary)
      let temporary = this.items.filter(t => t.temporary)[0]
      let same = items.length === value.urls.length &&
        (value.language || '') === (this.language || '')
      if (value.texts) {
        same = same && this.text && items.length === value.texts.length
      } else {
        same = same && !this.text
      }
      let keys = {}
      if (same) {
        items.forEach((t, i) => {
          keys[t.value] = t
          if (t.value.url !== value.urls[i]) {
            same = false
          }
        })
        if (value.texts) {
          items.forEach((t, i) => {
            if (t.value.text !== value.texts[i]) {
              same = false
            }
          })
        }
      }
      if (!temporary) {
        temporary = {
          key: Math.random() + '',
          temporary: true,
          value: {
            url: '',
            text: '',
          },
        }
        if (same) this.items.push(temporary)
      }
      if (!same) {
        let texts = value.texts || []
        items = value.urls.map((val, i) => {
          return keys[val] || {
            key: Math.random() + '',
            value: {
              url: val,
              text: texts[i] || '',
            },
          }
        })
        items.push(temporary)
        this.text = !!value.texts
        this.language = value.language || ''
        this.items = items
      }
    },
    itemsUpdated () {
      /* Called when this.items is updated, emits the new value */
      let temporary = this.items.filter(t => t.temporary)[0]
      if (temporary.value.url || temporary.value.text) {
        delete temporary.temporary
        temporary = null
      }
      if (!temporary) {
        temporary = {
          key: Math.random() + '',
          temporary: true,
          value: {
            url: '',
            text: '',
          },
        }
        this.items.push(temporary)
      }
      let out = { ...this.value }
      out.urls = this.items.filter(t => !t.temporary).map(t => t.value.url)
      if (this.text) {
        out.texts = this.items.filter(t => !t.temporary).map(t => t.value.text)
      } else delete out.texts
      if (this.language) {
        out.language = this.language
      } else {
        delete out.language
      }
      this.$emit('input', out)
    },
    deleteItem (key) {
      /* Deletes a url item */
      for (let i = 0; i < this.items.length; i++) {
        if (this.items[i].key === key &&
            !this.items[i].temporary) {
          this.items.splice(i, 1)
          return
        }
      }
      throw new TypeError('key "' + key + '" not found')
    },
    handleActivate (activate) {
      this.$emit('activate', activate !== false)
    },
  },
}
</script>
